import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Surface — a single floating pane of the desktop shell.
 *
 * The app used to render every route inside one big card owned by the project
 * Layout. Now the Layout is just a transparent flex row and each route supplies
 * its own surfaces, which sit directly on the ambient canvas with a gap between
 * them. Surface is what makes them look like the same object.
 *
 * It owns ONLY the skin — radius, hairline, material, elevation, clipping. It
 * deliberately owns no padding, no gap and no flex sizing, because those belong
 * to whoever is composing the row. Callers pass `flex-1 min-w-0` or a `width`.
 *
 * The `md:` prefixes on radius/border/shadow are the mobile-parity guarantee:
 * below 768px every surface is automatically full-bleed with no chrome, without
 * any caller having to remember.
 */

const BASE = 'relative flex min-h-0 min-w-0 flex-col overflow-hidden';

const SKIN = {
    // The thing you are in: conversation, file list, board. Glass, so the
    // project-tinted canvas reads faintly through it — but clearly darker than
    // the canvas, or the pane doesn't separate from its surroundings at all.
    primary:
        'bg-[hsl(240_11%_4%)]/80 backdrop-blur-md ' +
        'md:rounded-[20px] md:border md:border-white/[0.09] ' +
        'md:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.06),0_1px_2px_0_hsl(240_10%_0%/0.7),0_28px_70px_-20px_hsl(240_10%_0%/0.9)]',

    // The artifact being worked on. Opaque on purpose: its content (Monaco, a
    // task list) is opaque anyway, so a second backdrop-filter would cost a
    // whole blur pass to composite something nothing can see through.
    panel:
        'bg-[hsl(240_11%_5%)] ' +
        'md:rounded-[20px] md:border md:border-white/[0.11] ' +
        'md:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.07),0_1px_2px_0_hsl(240_10%_0%/0.7),0_32px_84px_-24px_hsl(240_10%_0%/0.95)]',

    // A subdivision of a route that still sits on the canvas in its own right:
    // a kanban column, a matrix quadrant. Three or four of these are on screen
    // at once, which drives both differences from `primary`.
    //
    // No backdrop-blur: four tiles would mean four blur passes for a material
    // almost entirely covered by opaque cards. Alpha over the canvas gets the
    // project tint through for free.
    //
    // No `md:` gating either — this is the one variant that must keep its chrome
    // on mobile. Below 640px the board is a horizontal snap-scroll carousel, and
    // full-bleed columns would run into each other with nothing to separate them.
    // The elevation is much shallower than `primary`'s as well: tiles are
    // coplanar neighbours, not panes floating over one another, and three deep
    // shadows at a 12px gap just pool into mud between the columns.
    // Only lightly translucent: a tile can carry its own colour wash (the
    // Eisenhower quadrants do), and at /85 a strongly tinted project canvas
    // bleeds through hard enough to drag every quadrant toward the same hue.
    // /92 keeps a hint of the ambience without eating the tile's own meaning.
    tile:
        'bg-[hsl(240_11%_5%)]/92 rounded-2xl border border-white/[0.08] ' +
        'shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.05),0_2px_10px_-3px_hsl(240_10%_0%/0.7)]',

    // Slim instrument strip above a route's content. Same glass as `primary`,
    // but a tighter shadow: the deep 70px falloff reads as "tall pane hovering"
    // and looks wrong under something 48px high. On mobile it keeps a bottom
    // hairline instead of going fully chrome-less, otherwise the controls bleed
    // into the content directly beneath them.
    bar:
        'bg-[hsl(240_11%_4%)]/80 backdrop-blur-md border-b border-white/[0.09] ' +
        'md:rounded-[20px] md:border md:border-white/[0.09] ' +
        'md:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.06),0_1px_2px_0_hsl(240_10%_0%/0.7),0_14px_36px_-14px_hsl(240_10%_0%/0.85)]',

    // Read-only share CTA strip above the row. Deliberately the quietest skin
    // in the set: it is marketing sitting on top of someone else's project, so
    // it borrows the same glass as `bar` and gets only a left-edge wash of the
    // project colour instead of the full-width gradient it used to carry — that
    // version read as a third pane and dominated the surfaces it sat above.
    banner:
        'bg-[hsl(240_11%_4%)]/85 bg-gradient-to-r from-primary/[0.10] via-primary/[0.03] to-transparent ' +
        'backdrop-blur-md border-b border-white/[0.09] ' +
        'md:rounded-[14px] md:border md:border-primary/20 ' +
        'md:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.05),0_10px_28px_-18px_hsl(240_10%_0%/0.9)]',
};

const MOBILE_TAKEOVER = 'fixed inset-0 z-50 w-full rounded-none border-0 bg-background';

export const Surface = forwardRef(function Surface(
    {
        as: As = 'section',
        variant = 'primary',
        // Artifact panels become full-screen takeovers on mobile rather than
        // shrinking into an unusable column. Defaults on for `panel`.
        mobileTakeover,
        width,
        className,
        style,
        children,
        ...rest
    },
    ref
) {
    const isMobile = useIsMobile();
    const takeover = (mobileTakeover ?? variant === 'panel') && isMobile;

    return (
        <As
            ref={ref}
            className={cn(BASE, SKIN[variant], takeover && MOBILE_TAKEOVER, className)}
            // Width is an inline style, never a class: Tailwind's JIT cannot see
            // an interpolated `w-[${n}px]`, and this is what the resize hook drives.
            style={!isMobile && width ? { width, ...style } : style}
            {...rest}
        >
            {children}
        </As>
    );
});

export default Surface;
